import { useQuery } from '@tanstack/react-query';
import { getSites, getSitesWithEUI, getSitesWithLatestData } from '@/api/sites';
import type { SitesResponseData, SiteWithEUI } from '@/api/sites/types';

/**
 * React Query hook for fetching sites
 */
export function useSites() {
  return useQuery<SitesResponseData, Error>({
    queryKey: ['sites'],
    queryFn: async () => {
      try {
        return await getSites();
      } catch (error) {
        console.error('Error in useSites hook:', error);
        throw error;
      }
    },
    staleTime: 60000, // Consider data fresh for 60 seconds
    retry: (failureCount, error) => {
      // Only retry network errors, not 4xx errors
      if (error.status && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * React Query hook for fetching sites with calculated EUI
 */
export function useSitesWithEUI() {
  return useQuery<SiteWithEUI[], Error>({
    queryKey: ['sites', 'with-eui'],
    queryFn: async () => {
      try {
        return await getSitesWithEUI();
      } catch (error) {
        console.error('Error in useSitesWithEUI hook:', error);
        throw error;
      }
    },
    staleTime: 60000, // Consider data fresh for 60 seconds
    retry: (failureCount, error) => {
      // Only retry network errors, not 4xx errors
      if (error.status && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * React Query hook for fetching sites with latest_data expanded
 */
export function useSitesWithLatestData() {
  return useQuery<SitesResponseData, Error>({
    queryKey: ['sites', 'with-latest-data'],
    queryFn: async () => {
      try {
        return await getSitesWithLatestData();
      } catch (error) {
        console.error('Error in useSitesWithLatestData hook:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    retry: (failureCount, error) => {
      // Only retry network errors, not 4xx errors
      if (error.status && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
}